import { MarketplaceUserRole } from 'APIs/marketplace.users/marketplace.users.types';
import Message from 'Components/Message';
import Modal from 'Components/Modal/Modal';
import { marketplaceUserRoles } from 'Constants/Constants';
import { FormItem, H5, Label } from 'Constants/styles';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';

import { FormCheckbox, FormControl, FormWrapper, VInput } from './styles';
import { UserFormProps, defaultUserRole } from './types';

type Props = {
  userFormProps: UserFormProps | null;
  saveMarketplaceHendler: (formData: UserFormProps) => void;
  modalErrorMsg: string;
  isModalLoading: boolean;
  setShowModal: (showModal: boolean) => void;
};

const UserModal = ({
  userFormProps,
  saveMarketplaceHendler,
  modalErrorMsg,
  isModalLoading,
  setShowModal,
}: Props): ReactElement => {
  const [isNewUser, setIsNewUser] = useState<boolean>(true);
  const [hideError, setHideError] = useState<boolean>(false);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().trim().required('First name is required'),
    lastName: Yup.string().trim().required('Last name is required'),
    email: Yup.string().trim().email('Must be a valid email').required('Email is required'),
    role: Yup.string().oneOf(
      marketplaceUserRoles.map((b) => b.value as MarketplaceUserRole),
      'Role is required',
    ),
  });

  const defaultValues: UserFormProps = {
    userId: null,
    firstName: '',
    lastName: '',
    email: '',
    role: defaultUserRole,
    enabled: true,
  };

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<UserFormProps>({
    resolver: yupResolver(validationSchema),
    defaultValues: userFormProps ?? defaultValues,
  });

  const onSubmit = useCallback(
    async (formData) => {
      saveMarketplaceHendler(formData);
    },
    [saveMarketplaceHendler],
  );

  const handleFormSubmit = useCallback(() => {
    return handleSubmit(onSubmit);
  }, [handleSubmit, onSubmit]);

  const handleCancel = () => {
    setHideError(true);
    setShowModal(false);
  };

  useEffect(() => {
    setIsNewUser(!(userFormProps && userFormProps.userId));
  }, [userFormProps]);

  useEffect(() => {
    if (modalErrorMsg) {
      setHideError(false);
    }
  }, [modalErrorMsg]);

  return (
    <Modal
      title={isNewUser ? 'Add new user' : 'Edit user'}
      cancelBtnText="Cancel"
      confirmBtnText={isNewUser ? 'Add' : 'Save'}
      handleCancel={handleCancel}
      handleSubmit={handleFormSubmit()}
      disableConfirmBtn={isModalLoading}
      isLoading={isModalLoading}
      large={false}
      testId={'marketplace-user-modal'}
    >
      {modalErrorMsg.length > 0 && !hideError && (
        <Message
          className="mt-2 mb-0"
          success={false}
          description={modalErrorMsg}
          handleClose={(e) => {
            e.preventDefault();
            setHideError(true);
          }}
        />
      )}
      <FormWrapper>
        {isNewUser ? (
          <>
            <FormItem>
              <Label>First name</Label>
              <VInput {...register('firstName')} isValid={!errors.firstName} />
              {errors.firstName && <span className="error-msg">{errors.firstName.message}</span>}
            </FormItem>
            <FormItem>
              <Label>Last name</Label>
              <VInput {...register('lastName')} isValid={!errors.lastName} />
              {errors.lastName && <span className="error-msg">{errors.lastName.message}</span>}
            </FormItem>
            <FormItem>
              <Label>Email address</Label>
              <VInput {...register('email')} isValid={!errors.email} />
              {errors.email && <span className="error-msg">{errors.email.message}</span>}
            </FormItem>
          </>
        ) : (
          <FormItem>
            <H5>{`${getValues('firstName')} ${getValues('lastName')}`}</H5>
            <span>{getValues('email')}</span>
          </FormItem>
        )}

        <FormItem>
          <Label>Role</Label>
          <FormControl {...register('role')}>
            {[...marketplaceUserRoles].map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </FormControl>
          {errors.role && <span className="error-msg">{errors.role.message}</span>}
        </FormItem>
        {!isNewUser ? (
          <FormItem>
            <Label>Enabled</Label>
            <FormCheckbox type="checkbox" {...register('enabled')} />
            {errors.enabled && <span className="error-msg">{errors.enabled.message}</span>}
          </FormItem>
        ) : null}
      </FormWrapper>
    </Modal>
  );
};

export default UserModal;
